import { useGetMyMicroHarvestQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fmtUsd } from "@/lib/format";

export default function Harvest() {
  const { data, isLoading, isError } = useGetMyMicroHarvestQuery(undefined, {
    pollingInterval: 30000,
  });

  if (isLoading) return <div className="p-6 text-muted">Loading harvest…</div>;
  if (isError || !data) {
    return <div className="p-6 text-muted">Harvest status unavailable.</div>;
  }

  const positions = data.state ?? [];
  const active = positions.find((p) => p.state !== "idle");

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Harvest</h1>
        <p className="text-sm text-muted">
          Your accumulate + take-profit engine — it buys a small tranche, rests a
          take-profit into strength (keeping a core), then rebuys the dip. The
          starting asset is chosen automatically from live market conditions.
        </p>
      </header>

      {!data.enabled && (
        <Card>
          <CardContent className="pt-5 text-sm text-muted">
            Harvest isn’t enabled on your account yet.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="pt-5">
            <div className="text-xs uppercase tracking-wide text-muted">Realized P&amp;L</div>
            <div className={`mt-1 text-2xl font-semibold ${data.realized_pnl_usd >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
              {fmtUsd(data.realized_pnl_usd)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="text-xs uppercase tracking-wide text-muted">Fills</div>
            <div className="mt-1 text-2xl font-semibold">{data.fills}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5">
            <div className="text-xs uppercase tracking-wide text-muted">Status</div>
            <div className="mt-1 text-2xl font-semibold">
              {active ? active.symbol : data.enabled ? "waiting" : "off"}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Positions</CardTitle>
        </CardHeader>
        <CardContent>
          {positions.length === 0 ? (
            <div className="text-sm text-muted">
              No harvest cycle yet — it starts once funds land and an asset is
              selected.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted">
                    <th className="py-1 pr-4">Asset</th>
                    <th className="py-1 pr-4">State</th>
                    <th className="py-1 pr-4">Entry</th>
                    <th className="py-1 pr-4">TP</th>
                    <th className="py-1 pr-4">Cycles</th>
                    <th className="py-1 pr-4">Realized</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p) => (
                    <tr key={p.symbol} className="border-t border-border">
                      <td className="py-1 pr-4 font-medium">{p.symbol}</td>
                      <td className="py-1 pr-4">{p.state}</td>
                      <td className="py-1 pr-4">{p.entry_price ? fmtUsd(p.entry_price) : "—"}</td>
                      <td className="py-1 pr-4">{p.tp_price ? fmtUsd(p.tp_price) : "—"}</td>
                      <td className="py-1 pr-4">{p.cycles ?? 0}</td>
                      <td className="py-1 pr-4">{fmtUsd(p.realized_pnl_usd ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
